import { toast } from 'sonner';
import { t, k } from '../../../localization/t';
import { playSound, playLightningImpact } from '../../../utils/game/sounds';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { computeGridPositions, getCellNumber } from '../../../utils/game/grid';
import { updateCardFrontTexture } from '../../../utils/game/cardMesh';
import { cellsReachable, countsAsUnit, currentRegion, elementsAtCell, hasActiveStealth, isAvatarInVoid, isEdgeBlockedForUnit, reachableLocationsInSteps, sampleCellGeometry, nearestCellAtWorld } from '../../../utils/game/engine/core';
import {
  OP,
  applyStatusToggle,
  pathfinderPlacementCells,
  bodyContainingCell, adjacentFloodTargets, nearbyFloodTargets,
  rubbleAtCell, adjacentRubbleCells, adjacentVoidCells,
  findControllerGeomancer, findControllerDruidFrontFace,
  findDeadFireMinions, sumFireThresholds, projectilePaths,
  isFireSite,
  applyMaskIdentity, restoreIdentity, isLegalMaskTarget,
  isMagicSpell,
  isMinionCard, copyCastCost,
  DRAGONLORD_AVATAR_ID, isDragonIdentityActive, isUniqueDragon, dragonSetAsidePool, snapshotIdentity,
  minionsToTapAtCell, markSkipNextUntap,
  sumAirThresholds, pickRandomOtherUnit, nearbyCells,
  evilCells, stepCellsTowardClosestEvil,
} from '../../../utils/game/engine/automation';
import { DRAGONLORD_RULES } from '../../../utils/game/avatarRules/dragonlord';
import {
  createDragonlordReadyGlow,
  updateDragonlordReadyGlow,
  disposeDragonlordReadyGlow,
} from '../../../utils/game/visualEffects/dragonlordReadyGlow';
import {
  createDragonSpiritEcho,
  updateDragonSpiritEcho,
  beginDragonSpiritEchoOutro,
  disposeDragonSpiritEcho,
} from '../../../utils/game/visualEffects/dragonSpiritEcho';
import { resolveLocalImageUrl } from '../../../utils/localApi';
import { chooseIntent } from '../../../utils/game/net/automationIntents';
import { spawnTokenIntent } from '../../../utils/game/net/manualIntents';
import { msg } from '../../../utils/game/logMessage';
import { cardLogLabel } from './logbookChat';
import * as authCardState from './authCardState';

// Avatar-specific ability orchestration, extracted from GameBoard.jsx:
// Avatar of Water / Waveshaper floods, Druid flip + Tawny summon,
// Pathfinder tap, Geomancer rubble flows, Witch curse, Flamecaller +
// Avatar of Fire projectiles, Imposter mask, and Archimago cast-copy.
//
// Every function takes the GameBoard instance as `host` and drives the
// shared primitives on it (_startCellPick, _spawnSiteAtCell,
// _spawnTokenAtPoint, _executeTargetOp, _applyTap, playerResources,
// effectLedger, sync). Pure rule helpers stay in engine/automation;
// this module only translates their results into board state, prompts,
// and log lines.

// v1.103. Avatar of Water: open a cell-pick constrained to sites
// cardinal-adjacent to the avatar's body of water. Pure helpers in
// engine/automation/ops/flood.js compute the body + valid targets;
// this method just translates the result into pendingCellTarget.
export function startAvatarOfWaterFlood(host, avatar, controller) {
  const valid = host._computeFloodCells(avatar, /* nearby = */ false);
  if (!valid) return;
  if (valid.size === 0) {
    host.logAction(k('{name}: no adjacent site to flood'), { name: avatar.name || 'Avatar of Water' });
    return;
  }
  host._startCellPick(avatar, controller, OP.AVATAR_OF_WATER_FLOOD, valid);
}


// v1.103. Shared helper for Avatar of Water + Waveshaper. Returns
// null when the avatar isn't on a water site (caller logs fizzle);
// otherwise returns a Set of valid cell keys. `nearby` switches the
// adjacency rule (Waveshaper) on or off (Avatar of Water).
export function computeFloodCells(host, avatar, nearby) {
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return new Set();
  const mesh = host.meshes.get(avatar.id);
  if (!mesh) { toast.message(t('Avatar not on the field.')); return null; }
  const sourceCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  if (!sourceCell) { toast.message(t('Avatar not on the grid.')); return null; }
  const getFullCard = host._getFullCard;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const body = bodyContainingCell(
    host.meshes, host._cellAtWorld, totalCols, totalRows, getFullCard, sourceCell,
  );
  if (!body) {
    toast.message(t('Avatar must be on a water site.'));
    host.logAction(k('{name}: no body of water (effect fizzles)'), { name: avatar.name || msg('Avatar') });
    return null;
  }
  const sitedCells = host._cellsWithSites();
  return nearby
    ? nearbyFloodTargets(body, sitedCells, totalCols, totalRows)
    : adjacentFloodTargets(body, sitedCells, totalCols, totalRows);
}


// v1.103. Flood the picked cell's site (the cell is guaranteed to
// hold a site by _startAvatarOfWaterFlood), unflood the previously
// flooded site (tracked on avatar._floodedSiteId. "until you do so
// again"), then pop a confirm prompt for the optional teleport.
export function resolveAvatarOfWaterFlood(host, avatar, controller, cell, sourceName) {
  const priorId = avatar._floodedSiteId;
  if (priorId) {
    const priorMesh = host.meshes.get(priorId);
    const priorCard = priorMesh?.userData?.cardInstance;
    if (priorCard && (priorCard.statuses || []).includes('flooded')) {
      const changed = applyStatusToggle(priorCard, 'flooded', false);
      if (changed) {
        host.rebuildStatusBadges(priorCard.id);
        host._broadcastCardStatus(priorCard.id, 'flooded', false);
      }
    }
    avatar._floodedSiteId = null;
  }

  let pickedSite = null;
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    const cardCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (!cardCell || cardCell.col !== cell.col || cardCell.row !== cell.row) continue;
    pickedSite = card;
    break;
  }
  if (!pickedSite) {
    host.logAction(k('{name}: no site at picked cell (effect fizzles)'), { name: sourceName });
    host.recomputeStaticAuras();
    return;
  }

  const changed = applyStatusToggle(pickedSite, 'flooded', true);
  if (changed) {
    host.rebuildStatusBadges(pickedSite.id);
    host._broadcastCardStatus(pickedSite.id, 'flooded', true);
  }
  avatar._floodedSiteId = pickedSite.id;
  host.logAction(k('{name}: floods {card} at [{cell}]'), { name: sourceName, card: cardLogLabel(pickedSite, msg('site')), cell: getCellNumber(cell) });
  host.recomputeStaticAuras();

  // "You may teleport there." Confirm prompt; on confirm we move
  // the avatar to the cell via _moveCardToCell, which already runs
  // landingRegionForMove (teleport allows region crossing) so the
  // avatar can submerge into the freshly-flooded site.
  const promptId = `flood-teleport-${avatar.id}-${Math.random().toString(36).slice(2, 8)}`;
  host._pushStorylineEvents([{
    id: promptId,
    cardInstance: avatar,
    cardName: sourceName,
    controller,
    sourceEntry: null,
    promptType: 'confirm',
    description: t('Teleport {name} to the flooded site?', { name: sourceName }),
    _avatarFloodTeleport: { cell, sourceName },
  }]);
}


// v1.103. Pathfinder. Pull the topmost atlas card into hand (must
// be a Site), register the pathfinder placement marker so the next
// site drop is gated to a cell cardinal-adjacent to this avatar and
// triggers the auto-move on commit. Activation fizzles silently
// (cost was already paid) when atlas is empty or its top isn't a Site.
// v1.103. Pathfinder's "Tap → If able, play the topmost site of your
// atlas to an adjacent location and move there." Mirrors Geomancer's
// Druid's front-face activated ability. "Tap → Summon Bruin here.
// Flip this card." The generic activate path already paid the tap;
// this handler runs the imperative steps: spawn Bruin at Druid's
// cell, then flip the avatar to its back face. `fromAbility: true`
// bypasses the `flipPolicy: 'ability_only'` lock that blocks
// accidental F-hotkey flips. `recomputeStaticAuras` fires so any
// back-face aura (Phase 2: site-granted enemy-enter damage) picks
// up immediately.
// Summon a catalog companion (Bruin / Tawny) at a world point. In an
// authoritative match the SERVER mints it, via the same manual spawnToken
// intent the Token Browser uses: `_spawnTokenAtPoint` mints a client-local id
// the server has never heard of, so the card never enters MatchState. It then
// has no mirror entry, `restPosesFromMirror` never stamps `card.cell`, and
// `applyComputedY` ranks every cell-less mesh BELOW the site stack. The
// companion visibly falls through the site and rests under it (GH-78). Worse,
// the opponent never sees her at all and she does not survive a reconnect.
// Sandbox / legacy keep the local mint (there is no server to ask).
function summonCompanionAt(host, cardRow, controllerRotated, point) {
  if (host._authZonesEnabled?.()) {
    const cell = host._cellAtWorld(point.x, point.z)
      || nearestCellAtWorld(getGameGrid(host.state.spawnConfig), point.x, point.z);
    if (cell) {
      host._sendMatchIntent(spawnTokenIntent({
        cardId: cardRow.unique_id,
        seat: controllerRotated ? 'p2' : 'p1',
        col: cell.col,
        row: cell.row,
      }));
      return;
    }
  }
  host._spawnTokenAtPoint(cardRow, controllerRotated, point);
}

export function doDruidFlip(host, druid) {
  if (!druid) return;
  const mesh = host.meshes.get(druid.id);
  if (!mesh) return;
  const bruinRow = host._findCardCatalogToken('Bruin');
  if (bruinRow) {
    summonCompanionAt(host, bruinRow, druid.rotated, mesh.position);
  } else {
    host.logSystem(k('Druid: Bruin token not found in catalog'));
  }
  host.flipCard(druid, mesh, { fromAbility: true });
  host.recomputeStaticAuras();
  host.logAction(k('{name}: summons Bruin and flips'), { name: cardLogLabel(druid, 'Druid') });
}


// replace-Rubble flow: peek the atlas top, open a cell-pick on
// cardinal-adjacent void cells, then resolve by spawning the site at
// the picked cell and moving the avatar there. Fizzles (cost already
// paid) when the atlas top isn't a Site or no adjacent void exists.
export function startPathfinderTap(host, avatar, controller) {
  const { top } = host._getControllerAtlas(avatar.rotated);
  if (!top) {
    host.logAction(k("{name}: atlas top isn't a Site (fizzles)"), { name: avatar.name || 'Pathfinder' });
    toast.message(t('No atlas site to play.'));
    return;
  }
  const mesh = host.meshes.get(avatar.id);
  if (!mesh) return;
  const sourceCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  if (!sourceCell) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  // First turn in the void: the codex forces the first site UNDER the
  // avatar (its own cell). In the void on a later turn (all sites
  // destroyed) the player isn't forced. Under OR adjacent. On a site,
  // play to an adjacent void and move there.
  const inVoid = isAvatarInVoid(avatar.rotated, host.meshes, host._cellAtWorld, host._getFullCard);
  const ctrlKey = avatar.rotated ? 'p2' : 'p1';
  const isFirstTurn = (host.state.turnNumbers?.[ctrlKey] || 0) <= 1;
  const validCells = pathfinderPlacementCells(
    sourceCell, inVoid, isFirstTurn, host._cellsWithSites(), colPositions.length - 1, rowPositions.length - 1,
  );
  if (validCells.size === 0) {
    host.logAction(k('{name}: no adjacent void to play into'), { name: avatar.name || 'Pathfinder' });
    toast.message(t('No adjacent void to place site.'));
    return;
  }
  host._startCellPick(avatar, controller, OP.PATHFINDER_TAP, validCells);
}


// v1.103. Pathfinder cell-pick resolver. Pops the atlas top, spawns
// it as a site at the picked cell with full site-play effects + realm-
// entry watchers, then auto-moves the Pathfinder there. Mirrors
// _resolveGeomancerReplaceRubble; the only difference is the absence
// of a Rubble banish (Pathfinder picks an empty void) and the
// appended _moveCardToCell at the end.
export function resolvePathfinderTap(host, avatar, controller, cell, sourceName) {
  const { atlas, top } = host._getControllerAtlas(avatar.rotated);
  if (!top) return;
  const topFull = top.fullCard;

  const mesh = host.meshes.get(avatar.id);
  const avatarCell = mesh ? host._cellAtWorld(mesh.position.x, mesh.position.z) : null;

  atlas.cards.pop();
  host.updatePileMeshes();

  host._spawnSiteAtCell(topFull, cell, {
    rotated: !!avatar.rotated,
    fireSiteEntry: true,
    logLine: msg('{name}: places {card} at [{cell}]', { name: sourceName, card: topFull.name, cell: getCellNumber(cell) }),
  });
  // First-turn under-avatar placement: the site lands on the avatar's
  // own cell, so there is no "move there" step. It's already there.
  if (!avatarCell || avatarCell.col !== cell.col || avatarCell.row !== cell.row) {
    host._moveCardToCell(avatar, cell, sourceName, k('moves'), 'normal');
  }
}


// Bladedancer's "After her first attack each turn, Bladedancer may
// take a step. When she does, she may attack a unit there." The step
// uses the real movement reach engine (regions, walls, airborne) with
// a 1-step budget. Her tapped state is irrelevant because the
// ability grants the step. The follow-up attack reuses the standard
// attack-target modal, filtered to units at the destination cell.
export function startBladedancerStep(host, avatar, controller) {
  const mesh = host.meshes.get(avatar.id);
  if (!mesh) return;
  const sourceCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  if (!sourceCell) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;
  const getFullCard = host._getFullCard;
  const fromRegion = currentRegion(avatar, sourceCell, host.meshes, host._cellAtWorld, getFullCard);
  const wallBlocker = (unit, fromC, toC) =>
    isEdgeBlockedForUnit(unit, fromC, toC, host.meshes, getFullCard);
  const reach = reachableLocationsInSteps(
    avatar, getFullCard(avatar.cardId),
    { col: sourceCell.col, row: sourceCell.row, region: fromRegion },
    1,
    host.meshes, host._cellAtWorld, totalCols, totalRows, getFullCard,
    wallBlocker,
  );
  const validCells = cellsReachable(reach);
  if (validCells.size === 0) {
    host.logAction(k('{name}: no legal step from here'), { name: avatar.name || 'Bladedancer' });
    toast.message(t('No legal step from here.'));
    return;
  }
  host._startCellPick(avatar, controller, OP.BLADEDANCER_STEP, validCells, {
    description: t('Choose a location to step to'),
  });
}


// Bladedancer cell-pick resolver: commit the step, then offer an
// attack against any enemy unit standing at the destination. The
// attack goes through the standard modal → declare pipeline, so
// defend / intercept / strike-back all behave like a normal fight
// (she is already tapped from the first attack; declare's tap is a
// no-op).
export function resolveBladedancerStep(host, avatar, controller, cell, sourceName) {
  const moved = host._moveCardToCell(avatar, cell, sourceName, k('steps'), 'normal');
  if (!moved) return;
  offerBladedancerAttack(host, avatar, cell);
}

// The optional follow-up attack at the step destination. Shared by the local
// resolver above and the auth path (authChoice opens it after replying to the
// server's bladedancer_step cell prompt. The declaration itself rides the
// normal server-validated combat intents).
export function offerBladedancerAttack(host, avatar, cell) {
  const recs = (host._combatTargetsFor(avatar, cell, 0) || []).filter((rec) => {
    const tc = rec.target?.card;
    if (!tc || tc.isSite) return false;
    if (!countsAsUnit(tc)) return false;
    const tMesh = host.meshes.get(tc.id);
    if (!tMesh) return false;
    const tCell = host._cellAtWorld(tMesh.position.x, tMesh.position.z);
    return !!tCell && tCell.col === cell.col && tCell.row === cell.row;
  });
  if (recs.length === 0) return;
  host._openCombatAttackModal(avatar, cell, recs, false);
}


// Sparkmage's "Tap → Target nearby location. Deal damage to another
// random unit there equal to the sum of (A) on spells you've cast this
// turn." The damage is computed up front from the per-turn cast
// history (recorded in fireOnSpellCast, cleared at turn end); the
// cell-pick is gated to nearby cells holding at least one other unit.
// Fizzles politely when the (A) sum is 0 or no nearby unit exists.
// the tap cost is already paid, matching the other avatar built-ins.
export function startSparkmageShock(host, avatar, controller) {
  const sourceName = avatar.name || 'Sparkmage';
  const catalog = host.props.sorceryCards || [];
  const castIds = host._spellsCastThisTurn?.[controller] || [];
  const damage = sumAirThresholds(castIds, catalog);
  if (damage <= 0) {
    host.logAction(k('{name}: no (A) on spells cast this turn (fizzles)'), { name: sourceName });
    toast.message(t('No (A) on spells cast this turn.'));
    return;
  }
  const mesh = host.meshes.get(avatar.id);
  if (!mesh) return;
  const sourceCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  if (!sourceCell) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const candidates = nearbyCells(sourceCell, colPositions.length - 1, rowPositions.length - 1);
  const validCells = new Set();
  for (const key of candidates) {
    const [col, row] = key.split(',').map(Number);
    if (unitsAtCell(host, col, row).some((u) => u.id !== avatar.id)) validCells.add(key);
  }
  if (validCells.size === 0) {
    host.logAction(k('{name}: no nearby unit to shock (fizzles)'), { name: sourceName });
    toast.message(t('No nearby unit to shock.'));
    return;
  }
  host._startCellPick(avatar, controller, OP.SPARKMAGE_SHOCK, validCells, {
    description: t('Target a nearby location'),
    args: { damage },
  });
}


// Sparkmage cell-pick resolver: random pick among the other units at
// the chosen location, then route the damage through the unified
// funnel (Ward / replacements / Death's Door all apply). The strike
// lands with a lightning bolt + thunderclap. Online the bolt rides the
// server's boardFx manifest (the seeded strike resolves server-side);
// this local path is sandbox-only.
export function resolveSparkmageShock(host, avatar, controller, cell, sourceName, damage) {
  const target = pickRandomOtherUnit(unitsAtCell(host, cell.col, cell.row), avatar.id);
  if (!target) {
    host.logAction(k('{name}: no unit at the picked location (fizzles)'), { name: sourceName });
    return;
  }
  const targetMesh = host.meshes.get(target.id);
  if (targetMesh && host.lightningLayer) {
    // Cosmetics must never block the damage. Swallow any VFX error.
    try {
      const { x, y, z } = targetMesh.position;
      host.lightningLayer.fire({ x, y, z, variant: 'sparkmage',
        onImpact: () => playLightningImpact({ variant: 'sparkmage' }),
      });
    } catch (err) {
      console.warn('[sparkmage] lightning fx failed', err);
    }
  }
  host.logAction(k('{name}: shocks {target} (random) for {n}'), { name: sourceName, target: cardLogLabel(target, msg('a unit')), n: damage });
  host._applyDamageToTarget(target, damage, {
    sourceLabel: sourceName,
    source: avatar,
  });
}


// Persecutor's chosen mode. 'step' opens a cell-pick on the 1-step
// reach cells that move strictly closer to the closest Evil (printed
// Demon/Monster/Undead, corrupted subtypes, and branded enemies all
// count). 'brand' opens the standard target pick and grants the
// `branded` status to the chosen enemy unit; the status clears at end
// of turn and the Evil filter check honors it meanwhile.
export function resolvePersecutorChoice(host, avatar, controller, choice) {
  const sourceName = avatar.name || 'Persecutor';
  if (choice === 'brand') {
    host._startTargeting(avatar, controller, OP.GRANT_STATUS_TO_TARGET, {
      keyword: 'branded',
      target: { scope: 'global', enemy: true, type: 'Unit' },
    }, null, null, { promptDescription: t('Brand an enemy. Your cards treat them as Evil this turn.') });
    return;
  }
  if (choice !== 'step') return;
  const mesh = host.meshes.get(avatar.id);
  if (!mesh) return;
  const sourceCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  if (!sourceCell) return;
  const boardCards = [];
  for (const [, m] of host.meshes) {
    const c = m?.userData?.cardInstance;
    if (!c) continue;
    const cCell = host._cellAtWorld(m.position.x, m.position.z);
    if (cCell) boardCards.push({ card: c, cell: cCell });
  }
  const targets = evilCells(avatar, boardCards);
  if (targets.length === 0) {
    host.logAction(k('{name}: no Evil on the board (fizzles)'), { name: sourceName });
    toast.message(t('No Evil on the board.'));
    return;
  }
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const getFullCard = host._getFullCard;
  const fromRegion = currentRegion(avatar, sourceCell, host.meshes, host._cellAtWorld, getFullCard);
  const wallBlocker = (unit, fromC, toC) =>
    isEdgeBlockedForUnit(unit, fromC, toC, host.meshes, getFullCard);
  const reach = reachableLocationsInSteps(
    avatar, getFullCard(avatar.cardId),
    { col: sourceCell.col, row: sourceCell.row, region: fromRegion },
    1,
    host.meshes, host._cellAtWorld, colPositions.length - 1, rowPositions.length - 1, getFullCard,
    wallBlocker,
  );
  const validCells = stepCellsTowardClosestEvil(cellsReachable(reach), sourceCell, targets);
  if (validCells.size === 0) {
    host.logAction(k('{name}: no step gets closer to the Evil (fizzles)'), { name: sourceName });
    toast.message(t('No step gets closer to the Evil.'));
    return;
  }
  host._startCellPick(avatar, controller, OP.PERSECUTOR_STEP, validCells, {
    description: t('Step toward the closest Evil'),
  });
}


function unitsAtCell(host, col, row) {
  const out = [];
  for (const [, mesh] of host.meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c || c.isSite) continue;
    if (!countsAsUnit(c)) continue;
    const cCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (cCell && cCell.col === col && cCell.row === row) out.push(c);
  }
  return out;
}


// v1.103. Geomancer second tap: open a cell-pick on cardinal-
// adjacent cells that contain a Rubble. Fizzles silently (cost was
// already paid) when atlas is empty / top is non-Site / no adjacent
// Rubble exists. Pickable cells are also gated to having a Site so
// the spawn-target is unambiguous (Rubble IS a site, so this is a
// no-op refinement. But keeps validCells.size honest).
export function startGeomancerReplaceRubble(host, avatar, controller) {
  const { top } = host._getControllerAtlas(avatar.rotated);
  if (!top) {
    host.logAction(k("{name}: atlas top isn't a Site (fizzles)"), { name: avatar.name || 'Geomancer' });
    toast.message(t('No atlas site to play.'));
    return;
  }
  const mesh = host.meshes.get(avatar.id);
  if (!mesh) return;
  const sourceCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
  if (!sourceCell) return;
  const validCells = adjacentRubbleCells(sourceCell, host.meshes, host._cellAtWorld);
  if (validCells.size === 0) {
    host.logAction(k('{name}: no adjacent Rubble to replace'), { name: avatar.name || 'Geomancer' });
    toast.message(t('No adjacent Rubble to replace.'));
    return;
  }
  host._startCellPick(avatar, controller, OP.GEOMANCER_REPLACE_RUBBLE, validCells);
}


// v1.103. Resolve Geomancer's "Replace Rubble with topmost atlas
// site." Banishes the Rubble, pops the atlas top into a fresh site
// instance at the picked cell, then runs site-play effects + realm-
// entry watchers so the new site participates in affinity + triggers.
export function resolveGeomancerReplaceRubble(host, avatar, controller, cell, sourceName) {
  const rubble = rubbleAtCell(host.meshes, host._cellAtWorld, cell.col, cell.row);
  if (!rubble) {
    host.logAction(k('{name}: no Rubble at picked cell (fizzles)'), { name: sourceName });
    return;
  }
  const { atlas, top } = host._getControllerAtlas(avatar.rotated);
  if (!top) return;
  const topFull = top.fullCard;

  atlas.cards.pop();
  host.updatePileMeshes();

  host._spawnSiteAtCell(topFull, cell, {
    rotated: !!avatar.rotated,
    replacingSite: rubble,
    replacingDest: 'Banished',
    fireSiteEntry: true,
    logLine: msg('{name}: replaces Rubble at [{cell}] with {card}', { name: sourceName, cell: getCellNumber(cell), card: topFull.name }),
  });
}


// Geomancer earth-site bonus: spawn one Rubble at the picked void
// cell. Uses the shared `_spawnSiteAtCell` primitive so the Rubble
// is materialised by the same recipe as one produced by `turnToRubble`.
export function spawnRubbleAtCell(host, cell, rotated, sourceName) {
  const rubbleCard = host._getFullCard('sorcery-rubble');
  if (!rubbleCard) {
    console.warn('[spawnRubbleAtCell] Rubble card not found in sorceryCards');
    return null;
  }
  return host._spawnSiteAtCell(rubbleCard, cell, {
    rotated: !!rotated,
    fireSiteEntry: false,
    logLine: msg('{name}: fills [{cell}] with Rubble', { name: sourceName || 'Geomancer', cell: getCellNumber(cell) }),
  });
}


// v1.103. Fire Geomancer's earth-site bonus when a fresh site lands.
// Scans the controller's avatars for a Geomancer; if found and the
// landing cell has earth affinity, opens the cursor-preview spawn
// queue (same UX as Necromancer summons) on cardinal-adjacent void
// cells. Silently no-ops when the controller doesn't have a
// Geomancer or no adjacent void exists.
// Druid front-face passive. "Tap → Play or draw a site. If this is
// your first turn, summon Tawny here." Fires from the fresh-play
// commit alongside Geomancer's earth-site bonus. Gates:
//   0. Automations are on for the session (qa#129). Tawny is hardcoded
//      avatar logic rather than a catalog entry, so she never passes
//      through applyCardAutomation's shouldRunAutomation chokepoint and
//      was the one avatar passive still summoning itself at a manual
//      table. Geomancer's twin gets this for free: the server owns his
//      Rubble and gates it there (qa#117), while Tawny has no server
//      path at all, so this client call is her only source.
//   1. Controller has a front-face Druid in play (back-face Druid
//      has different rules text; the Tawny clause is gone).
//   2. It's the controller's FIRST turn. `turnNumbers[controller]
//      === 1`. Sorcery counts each player's turns independently, so
//      this fires regardless of whether the controller went first or
//      second; it's locked to that player's opening turn.
//   3. The played site has a real cell (sanity check).
// On all gates passing, Tawny spawns at the played site's cell.
// The spawn routes through `summonCompanionAt` so Tawny behaves
// like any other catalog Beast token. And, in an auth match, is
// minted by the server rather than only on this client.
export function maybeFireDruidTawnySummon(host, siteCard) {
  if (!siteCard) return;
  if (host._automationsEnabled?.() === false) return;
  // Rubble-spawned sites (Geomancer's earth bonus, turnToRubble) go
  // through their own spawn helpers and never reach the fresh-play
  // commit, so this check is belt-and-suspenders.
  if (siteCard.cardId === 'sorcery-rubble') return;
  const controller = siteCard.rotated ? 'p2' : 'p1';
  const turn = host.state.turnNumbers?.[controller] || 0;
  if (turn !== 1) return;
  const druid = findControllerDruidFrontFace(host.meshes, !!siteCard.rotated);
  if (!druid) return;
  const siteMesh = host.meshes.get(siteCard.id);
  if (!siteMesh) return;
  const tawnyRow = host._findCardCatalogToken('Tawny');
  if (!tawnyRow) {
    host.logSystem(k('Druid: Tawny token not found in catalog'));
    return;
  }
  summonCompanionAt(host, tawnyRow, !!siteCard.rotated, siteMesh.position);
  host.logAction(k('{name}: summons Tawny on the new site'), { name: cardLogLabel(druid, 'Druid') });
}


export function maybeFireGeomancerEarthBonus(host, siteCard) {
  const controllerRotated = !!siteCard.rotated;
  const geomancer = findControllerGeomancer(host.meshes, controllerRotated);
  if (!geomancer) return;
  const siteMesh = host.meshes.get(siteCard.id);
  if (!siteMesh) return;
  const siteCell = host._cellAtWorld(siteMesh.position.x, siteMesh.position.z);
  if (!siteCell) return;
  const getFullCard = host._getFullCard;
  const elements = elementsAtCell(host.meshes, host._cellAtWorld, siteCell.col, siteCell.row, getFullCard);
  if ((elements.earth || 0) <= 0) return;

  const avMesh = host.meshes.get(geomancer.id);
  const avCell = avMesh && host._cellAtWorld(avMesh.position.x, avMesh.position.z);
  if (!avCell) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const validCells = adjacentVoidCells(
    avCell, host._cellsWithSites(), colPositions.length - 1, rowPositions.length - 1,
  );
  if (validCells.size === 0) {
    host.logAction(k('Geomancer: no adjacent void to fill with Rubble'));
    return;
  }
  const rubbleRow = host._getFullCard('sorcery-rubble');
  if (!rubbleRow) return;
  const sourceLabel = geomancer.name || 'Geomancer';
  host._startSummonPlacements(
    [{
      tokenRow: rubbleRow,
      controllerRotated,
      validCells,
      sourceLabel,
      customSpawn: (cell) => host._spawnRubbleAtCell(cell, controllerRotated, sourceLabel),
    }],
    rubbleRow.name || 'Rubble',
  );
}


// v1.103. Witch curse resolver. Dispatches the chosen curse to the
// opponent. 'cost' adds a cost modifier for the opponent's next turn
// (mirrors Templar/Tax Collector ledger entry). 'life' deals 2 life
// via _dealLifeLoss. 'power' applies a -3 power debuff to the
// opponent's Avatar that reverts at the end of the opponent's NEXT
// turn. Matching codex "until your next turn" (= until the start of
// the caster's next turn).
export function resolveWitchCurse(host, sourceName, controller, opponent, choice) {
  if (choice === 'cost') {
    const targetTurnNumber = host.state.turnNumbers[opponent] || 0;
    const expiresAtTurn = targetTurnNumber + 1;
    const effect = { op: OP.MODIFY_CAST_COST, args: { delta: 1, target_player: 'opponent' } };
    const ledgerEntry = {
      sourceCardName: sourceName,
      controller,
      targetPlayer: opponent,
      effect,
      expiry: { type: 'turn_end', player: opponent, turnNumber: expiresAtTurn },
    };
    host.effectLedger.add(ledgerEntry);
    const opponentLabel = opponent === 'p1' ? msg('Player 1') : msg('Player 2');
    host.logAction(k("{name}: {who}'s spells cost 1 more on their next turn"), { name: sourceName, who: opponentLabel });
    return;
  }
  if (choice === 'life') {
    host.playerResources.loseLife(opponent, 2, msg('{name} curse', { name: sourceName }));
    return;
  }
  if (choice === 'power') {
    const opponentRotated = opponent === 'p2';
    const avatar = host._findAvatarCard(opponentRotated);
    if (!avatar) {
      host.logAction(k('{name}: opponent has no Avatar in play (curse fizzles)'), { name: sourceName });
      return;
    }
    const opponentTurnNumber = host.state.turnNumbers[opponent] || 0;
    // The debuff persists until the end of opponent's NEXT turn
    // (= start of caster's next turn). If opponent is mid-turn, end
    // of THIS turn ends it; else end of opponent's upcoming turn.
    const expiresAtTurn = opponentTurnNumber + 1;
    host._applyDelayedStatBuff(avatar, -3, { revertPlayer: opponent, revertAtTurnNumber: expiresAtTurn });
    host.logAction(k('{name}: {target} has -3 power until your next turn'), { name: sourceName, target: avatar.name || msg('opponent Avatar') });
  }
}


// v1.111. Flamecaller's "Tap, Banish all your dead fire minions →
// Shoot a projectile. It deals damage equal to the sum of their (F)."
//
// Codex Projectile: flight starts at the shooter's cell, travels in
// a single cardinal direction within the shooter's region, stops at
// the first unit hit, ignores allies at the starting cell, and
// cannot hit Stealth. Avatar is surface-only, so the projectile is
// surface-only. Subsurface units don't block and aren't valid
// targets.
//
// Cost ordering follows the printed text: Tap (already paid before
// dispatch) THEN banish all dead fire minions THEN choose the
// projectile's target. Both costs are paid up-front and stay paid
// even if no candidate target exists, matching codex "you pay costs
// first, then resolve the effect".
export function startFlamecallerFireBlast(host, avatar, controller) {
  const sourceName = avatar.name || 'Flamecaller';
  const ownerRotated = controller === 'p2';
  const cemetery = host.findOrCreateCemetery(ownerRotated);
  const catalog = host.props.sorceryCards || [];
  const deadFire = findDeadFireMinions(cemetery.cards, catalog);
  const damage = sumFireThresholds(deadFire, catalog);

  // Pay the banish cost. Every Fire Minion currently in cemetery
  // moves to Banished. With zero Fire Minions the cost is vacuous;
  // either way the projectile damage is the sum of (F).
  if (deadFire.length > 0) {
    const banished = host.findOrCreateBanished(ownerRotated);
    for (const card of deadFire) {
      const idx = cemetery.cards.findIndex((c) => c.id === card.id);
      if (idx !== -1) cemetery.cards.splice(idx, 1);
      banished.cards.push(card);
    }
    host.updatePileMeshes();
    if (deadFire.length === 1) {
      host.logAction(k('{name}: banished 1 fire minion (sum (F) = {n})'), { name: sourceName, n: damage });
    } else {
      host.logAction(k('{name}: banished {count} fire minions (sum (F) = {n})'), { name: sourceName, count: deadFire.length, n: damage });
    }
  } else {
    host.logAction(k('{name}: no dead fire minions to banish (projectile deals 0)'), { name: sourceName });
  }

  // Locate the shooter and the grid bounds.
  const sourceMesh = host.meshes.get(avatar.id);
  if (!sourceMesh) return;
  const startCell = host._cellAtWorld(sourceMesh.position.x, sourceMesh.position.z);
  if (!startCell) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;

  // Build a cell → surface-unit-instances map. Surface = NOT subsurface
  // (avatars and non-burrowed / non-submerged minions). Excludes the
  // shooter itself and any allies at the shooter's cell per codex.
  const unitsByCell = new Map();
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (card.id === avatar.id) continue;
    if (!countsAsUnit(card)) continue;
    if (card.subsurface) continue;
    const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    const key = `${cell.col},${cell.row}`;
    const list = unitsByCell.get(key) || [];
    list.push(card);
    unitsByCell.set(key, list);
  }

  // Hittable = unit can be impacted by a projectile per codex. Stealth
  // protects from projectiles. (Codex: "Projectiles cannot hit minions
  // with Stealth.") Disabled units stay hittable. Only their
  // abilities are suppressed.
  const isHittable = (card) => !hasActiveStealth(card);

  const paths = projectilePaths({
    startCell, totalCols, totalRows, unitsByCell, isHittable,
  });

  if (paths.length === 0) {
    host.logAction(k('{name}: no valid projectile target (fizzles)'), { name: sourceName });
    return;
  }

  // validCells unions every cell the projectile would CROSS across
  // all four directions. The player can click any cell along a
  // direction's trail to fire that direction. They don't have to
  // pin the impact cell exactly. _resolveFlamecallerFireBlast maps
  // the picked cell back to its direction, then to the impact unit.
  const validCells = new Set();
  for (const path of paths) {
    for (const c of path.pathCells) validCells.add(`${c.col},${c.row}`);
  }

  host._startCellPick(avatar, controller, OP.FLAMECALLER_FIRE_BLAST, validCells, {
    description: damage > 0
      ? t('Shoot a projectile ({n} damage)', { n: damage })
      : t('Shoot a projectile (0 damage)'),
    args: { amount: damage },
    extra: { projectilePaths: paths },
  });
}


// ── Imposter Mask ─────────────────────────────────────────────────
// The (3) Mask ability opens the controller's Collection pile in the
// existing PileSearchDialog. Clicks on Avatar cards route through
// `_resolveMaskPick`, which banishes the picked card and swaps the
// imposter's identity to the masked avatar. The cost was already
// paid in `_activateAbility` (the menu gate guarantees a legal target).
export function startImposterMask(host, avatar, controller, manaCost = 0) {
  const pile = host._collectionPileFor(controller);
  if (!pile) {
    // _canActivateEntry should have suppressed the activation already
    // but a remote/network race could let the click through with an
    // empty collection. No card to banish → nothing to do.
    host.logAction(k('{name}: no Collection found'), { name: avatar.name || 'Imposter' });
    return;
  }
  host.setState({
    searchPile: pile,
    searchQuery: '',
    pendingMaskPick: {
      sourceCardId: avatar.id,
      controller,
      pileId: pile.id,
      manaCost,
    },
  });
  host.logAction(k('{name}: choose an Avatar to mask'), { name: avatar.name || 'Imposter' });
}


export function resolveMaskPick(host, pickedCard) {
  const pending = host.state.pendingMaskPick;
  if (!pending) return;
  const catalog = host.props.sorceryCards || [];
  const full = catalog.find((c) => c.unique_id === pickedCard.cardId);
  if (!isLegalMaskTarget(full)) {
    toast.error(t('Pick an Avatar.'));
    return;
  }
  const pile = host.state.gameState.piles.find((p) => p.id === pending.pileId);
  const imposter = host._findCardById(pending.sourceCardId);
  if (!pile || !imposter) {
    host.setState({ pendingMaskPick: null, searchPile: null, searchQuery: '' });
    return;
  }
  // Pay mana NOW that we know a valid Avatar is being applied. The
  // original cost-validation in `_activateAbility` proved the pool
  // was high enough at the time of the click; re-check here so a
  // mid-flow mana spend (e.g. a separate activation between opening
  // the picker and confirming) can't drive the pool negative.
  const need = pending.manaCost || 0;
  if (need > 0) {
    const have = host.playerResources.getMana(pending.controller);
    if (have < need) {
      toast.error(t('Need {need} mana. You have {have}.', { need, have }));
      return;
    }
    host.playerResources.payMana(pending.controller, need);
  }
  // Banish first so the cost is realized before the visual swap,
  // matching the codex "Banish ... to mask yourself" ordering and
  // keeping the log readable.
  host.banishCardFromPile(pile, pickedCard);
  host._putOnMask(imposter, full);
  host.setState({ pendingMaskPick: null, searchPile: null, searchQuery: '' });
}


// Mutates the imposter card instance in place. `applyMaskIdentity`
// overwrites cardId / name / image / subTypes / elements / printed
// keywords / Movement+N / Ranged so every downstream rule pipeline
// (`resolveAvatarRules`, sidecar automation, `recomputeStaticAuras`,
// `_activatedEntriesFor`) automatically resolves to the masked
// avatar. The previous identity lands in `_originalIdentity` so the
// damage-triggered revert can put it back.
export function putOnMask(host, imposter, sourceCard) {
  if (!imposter || !sourceCard) return;
  // Successive masks: keep the FIRST identity (the imposter's true
  // face) so revert always lands on the imposter, not the previous
  // mask. The "new mask" overwrites the current state, not the
  // baseline.
  const priorSnapshot = applyMaskIdentity(imposter, sourceCard);
  if (!imposter._originalIdentity) {
    imposter._originalIdentity = priorSnapshot;
  }
  const mesh = host.meshes.get(imposter.id);
  if (mesh) updateCardFrontTexture(mesh, imposter.imageUrl);
  host._refreshMovementHUD(imposter.id);
  host.logAction(k('{name} puts on the mask of {card}'), { name: imposter._originalIdentity?.name || 'Imposter', card: cardLogLabel(sourceCard) });
  // Statics on the masked avatar may project new auras (e.g.
  // Elementalist's threshold grant). Recompute so the masked
  // avatar's rules go live immediately.
  host.recomputeStaticAuras?.();
  if (host._authZonesEnabled?.()) {
    host._sendMatchCommand(authCardState.identityCommandFromCard(imposter));
  }
}


// Damage trigger → mask comes off. Restores the imposter's true face
// from `_originalIdentity` and repaints the mesh. No-op when the
// imposter is bare-faced or the card isn't an imposter.
export function takeOffMask(host, imposter) {
  if (!imposter || !imposter._isImposter) return;
  const snapshot = imposter._originalIdentity;
  if (!snapshot) return;
  restoreIdentity(imposter, snapshot);
  imposter._originalIdentity = null;
  const mesh = host.meshes.get(imposter.id);
  if (mesh) updateCardFrontTexture(mesh, imposter.imageUrl);
  host._refreshMovementHUD(imposter.id);
  host.logAction(k('{name} sheds the mask'), { name: imposter.name });
  host.recomputeStaticAuras?.();
  if (host._authZonesEnabled?.()) {
    host._sendMatchCommand(authCardState.identityRestoreCommand(imposter.id));
  }
}


// Archimago. "Banish three magic spells in your cemetery → Cast a
// copy of one of them." Two-phase resolution:
//   1. Opens the controller's Cemetery in PileSearchDialog with a
//      Magic-only pick gate. Each click banishes that Magic spell
//      (moving it from Cemetery → Banished) and appends it to the
//      `picked` list. When three picks have landed the picker
//      transitions to phase 2.
//   2. The three banished spells are displayed in a synthetic pile
//      (NOT in gameState.piles. Purely a transient picker source).
//      The player picks one to cast a copy of. The chosen card's
//      self-controller standalone effects (life / draw / mana)
//      resolve via `_resolveStandaloneSpellEffects`; the copy never
//      enters the realm and never lands in a pile (it's a virtual
//      cast that just resolves its effect).
//
// ESC / close at any point bails: cards already banished in phase 1
// stay banished (the cost was partially paid). Matches sandbox
// expectations. Players don't get a free undo on a cost they
// already committed to.
export function startArchimagoCastCopy(host, avatar, controller) {
  const ownerRotated = controller === 'p2';
  const cemetery = host.findOrCreateCemetery(ownerRotated);
  if (!cemetery) {
    host.logAction(k('{name}: no Cemetery found'), { name: avatar.name || 'Archimago' });
    return;
  }
  host.setState({
    searchPile: cemetery,
    searchQuery: '',
    pendingArchimagoBanish: {
      sourceCardId: avatar.id,
      controller,
      cemeteryPileId: cemetery.id,
      picked: [],
    },
  });
  host.logAction(k('{name}: choose 3 magic spells to banish'), { name: avatar.name || 'Archimago' });
}


export function resolveArchimagoBanishPick(host, pickedCard) {
  const pending = host.state.pendingArchimagoBanish;
  if (!pending) return;
  const catalog = host.props.sorceryCards || [];
  if (!isMagicSpell(pickedCard, catalog)) {
    toast.error(t('Pick a magic spell.'));
    return;
  }
  const cemetery = host.state.gameState.piles.find((p) => p.id === pending.cemeteryPileId);
  const avatar = host._findCardById(pending.sourceCardId);
  if (!cemetery || !avatar) {
    host.setState({ pendingArchimagoBanish: null, searchPile: null, searchQuery: '' });
    return;
  }
  const idx = cemetery.cards.findIndex((c) => c.id === pickedCard.id);
  if (idx === -1) return;
  const banished = host.findOrCreateBanished(pending.controller === 'p2');
  const [removed] = cemetery.cards.splice(idx, 1);
  banished.cards.push(removed);
  host.updatePileMeshes();

  const nextPicked = [...pending.picked, removed];
  host.logAction(
    k('{name}: banished {card} ({n}/3)'),
    { name: avatar.name || 'Archimago', card: cardLogLabel(removed || pickedCard), n: nextPicked.length },
  );

  if (nextPicked.length < 3) {
    host.setState({
      pendingArchimagoBanish: { ...pending, picked: nextPicked },
    });
    return;
  }

  // Phase 2. Pick one of the three to cast a copy of. The synthetic
  // pile sits OUTSIDE gameState.piles; PileSearchDialog only reads
  // `pile.cards` / `pile.name` / `pile.id`, so a transient object
  // satisfies the renderer without leaking into match state.
  const copyPile = {
    id: `archimago-copy-picker-${avatar.id}`,
    name: t('Cast a copy'),
    cards: nextPicked,
  };
  host.setState({
    pendingArchimagoBanish: null,
    pendingArchimagoCopy: {
      sourceCardId: avatar.id,
      controller: pending.controller,
      candidates: nextPicked,
    },
    searchPile: copyPile,
    searchQuery: '',
  });
  host.logAction(k('{name}: choose a spell to copy'), { name: avatar.name || 'Archimago' });
}


export function resolveArchimagoCopyPick(host, pickedCard) {
  const pending = host.state.pendingArchimagoCopy;
  if (!pending) return;
  const candidate = pending.candidates.find((c) => c.id === pickedCard.id);
  if (!candidate) {
    toast.error(t('Pick a banished spell.'));
    return;
  }
  const avatar = host._findCardById(pending.sourceCardId);
  const sourceName = avatar?.name || 'Archimago';
  const fullCard = host._getFullCard(candidate.cardId);
  if (!fullCard) {
    host.setState({ pendingArchimagoCopy: null, searchPile: null, searchQuery: '' });
    return;
  }
  const controller = pending.controller;
  const ownerRotated = controller === 'p2';

  host.setState({
    pendingArchimagoCopy: null,
    searchPile: null,
    searchQuery: '',
  });

  const printing = fullCard.printings?.find((p) => p.foiling === 'S')
    || fullCard.printings?.[0]
    || null;
  const clone = {
    id: `magic-copy-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    cardId: candidate.cardId,
    name: candidate.name || fullCard.name,
    imageUrl: candidate.imageUrl || printing?.image_url || '',
    printingId: candidate.printingId || printing?.unique_id || null,
    foiling: candidate.foiling || printing?.foiling || 'S',
    type: fullCard.type,
    y: 0,
    rotated: ownerRotated,
  };
  host.logAction(k('{name}: casts a copy of {card}'), { name: sourceName, card: fullCard.name || cardLogLabel(candidate) });

  // Archimago copies MAGIC spells. The copy takes the same cast path as a
  // hand-played magic (_castMagicFromHand): it flies into the storyline with
  // no cell pick, then settles on the avatar's square once it resolves.
  if (fullCard.type === 'Magic') {
    clone.x = avatar?.x ?? 0;
    clone.z = avatar?.z ?? 0;
    host._castMagicFromHand(clone);
    return;
  }

  // Defensive fallback for a non-magic copy: place it on the board via the
  // standard free-placement click-to-place UX (unreachable today. The
  // picker only offers magics).
  host._startSummonPlacements(
    [{
      unrestricted: true,
      pileEntry: { imageUrl: clone.imageUrl || null },
      sourceLabel: sourceName,
      customSpawn: (cell) => {
        const grid = getGameGrid(host.state.spawnConfig);
        const geo = grid && sampleCellGeometry(grid, cell.col, cell.row);
        if (!geo) return;
        clone.x = (geo.tl.x + geo.tr.x + geo.bl.x + geo.br.x) / 4;
        clone.z = (geo.tl.z + geo.tr.z + geo.bl.z + geo.br.z) / 4;
        host.addCardToTable(clone, true, { logVerb: k('Casts copy of') });
      },
    }],
    fullCard.name || candidate.name || 'spell copy',
  );
}


// Deathspeaker. "You may banish a dead minion each turn to cast a
// copy of it, and for (0) if you're on Death's Door. The copy enters
// the realm, uses its Genesis, then is banished." Opens the
// controller's Cemetery in PileSearchDialog with a Minion-only pick
// gate; once-per-turn is enforced by the generic activated pipeline.
export function startDeathspeakerCastCopy(host, avatar, controller) {
  const ownerRotated = controller === 'p2';
  const cemetery = host.findOrCreateCemetery(ownerRotated);
  if (!cemetery) {
    host.logAction(k('{name}: no Cemetery found'), { name: avatar.name || 'Deathspeaker' });
    return;
  }
  host.setState({
    searchPile: cemetery,
    searchQuery: '',
    pendingDeathspeakerPick: {
      sourceCardId: avatar.id,
      controller,
      cemeteryPileId: cemetery.id,
    },
  });
  host.logAction(k('{name}: choose a dead minion to copy'), { name: avatar.name || 'Deathspeaker' });
}


// Deathspeaker pick resolver. Pays the copy's cost ((0) at Death's
// Door), banishes the dead minion, then click-to-places the copy.
// The spawned copy runs the standard addCardToTable pipeline. Its
// Genesis (on_play automation) fires like any cast minion. And is
// banished immediately after, per the card's "enters the realm, uses
// its Genesis, then is banished".
export function resolveDeathspeakerPick(host, pickedCard) {
  const pending = host.state.pendingDeathspeakerPick;
  if (!pending) return;
  const catalog = host.props.sorceryCards || [];
  if (!isMinionCard(pickedCard, catalog)) {
    toast.error(t('Pick a minion.'));
    return;
  }
  const cemetery = host.state.gameState.piles.find((p) => p.id === pending.cemeteryPileId);
  const avatar = host._findCardById(pending.sourceCardId);
  if (!cemetery || !avatar) {
    host.setState({ pendingDeathspeakerPick: null, searchPile: null, searchQuery: '' });
    return;
  }
  const controller = pending.controller;
  const sourceName = avatar.name || 'Deathspeaker';
  const fullCard = host._getFullCard(pickedCard.cardId);
  if (!fullCard) return;

  // Cost is computed and paid at pick time. Death's Door state and
  // the mana pool can both move between opening the picker and the
  // click (mirrors the Imposter mask deferred-payment pattern).
  const need = copyCastCost(pickedCard, catalog, host.playerResources.isAtDeathsDoor(controller));
  if (need > 0) {
    const have = host.playerResources.getMana(controller);
    if (have < need) {
      toast.error(t('Need {need} mana. You have {have}.', { need, have }));
      return;
    }
    host.playerResources.payMana(controller, need);
  }

  // The pick is the commitment point: burn the once-per-turn budget
  // here (same `act:` key the activation gate checks) so cancelling
  // the picker stayed free.
  if (!avatar._abilitiesUsedThisTurn) avatar._abilitiesUsedThisTurn = new Set();
  avatar._abilitiesUsedThisTurn.add('act:__builtin_deathspeaker_cast_copy');
  host._broadcastAbilitiesUsed?.(avatar);

  // Banish the dead minion (the cost's card half) before the copy
  // materializes.
  const idx = cemetery.cards.findIndex((c) => c.id === pickedCard.id);
  if (idx === -1) return;
  const banishedPile = host.findOrCreateBanished(controller === 'p2');
  const [removed] = cemetery.cards.splice(idx, 1);
  banishedPile.cards.push(removed);
  host.updatePileMeshes();
  if (need > 0) {
    host.logAction(k('{name}: banished {card} and paid ({n})'), { name: sourceName, card: cardLogLabel(removed, msg('a minion')), n: need });
  } else {
    host.logAction(k('{name}: banished {card} for (0)'), { name: sourceName, card: cardLogLabel(removed, msg('a minion')) });
  }

  host.setState({ pendingDeathspeakerPick: null, searchPile: null, searchQuery: '' });

  const ownerRotated = controller === 'p2';
  host._startSummonPlacements(
    [{
      unrestricted: true,
      pileEntry: { imageUrl: removed.imageUrl || fullCard.printings?.[0]?.image_url || null },
      sourceLabel: sourceName,
      customSpawn: (cell) => {
        const grid = getGameGrid(host.state.spawnConfig);
        const geo = grid && sampleCellGeometry(grid, cell.col, cell.row);
        if (!geo) return;
        const cx = (geo.tl.x + geo.tr.x + geo.bl.x + geo.br.x) / 4;
        const cz = (geo.tl.z + geo.tr.z + geo.bl.z + geo.br.z) / 4;
        const printing = fullCard.printings?.find((p) => p.foiling === 'S')
          || fullCard.printings?.[0]
          || null;
        const clone = {
          id: `minion-copy-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          cardId: removed.cardId,
          name: removed.name || fullCard.name,
          imageUrl: removed.imageUrl || printing?.image_url || '',
          printingId: removed.printingId || printing?.unique_id || null,
          foiling: removed.foiling || printing?.foiling || 'S',
          type: fullCard.type,
          x: cx,
          y: 0,
          z: cz,
          rotated: ownerRotated,
        };
        host.logAction(k('{name}: casts a copy of {card}'), { name: sourceName, card: fullCard.name || cardLogLabel(removed) });
        host.addCardToTable(clone, true, { logVerb: k('Casts copy of') });
        // "…uses its Genesis, then is banished." The copy is stamped
        // for banish-after-Genesis; maybeDisposeFinishedSpell sends it
        // to Banished as soon as no Genesis prompt references it.
        // immediately when Genesis queued nothing.
        if (!host._banishAfterGenesis) host._banishAfterGenesis = new Set();
        host._banishAfterGenesis.add(clone.id);
        host._maybeDisposeFinishedSpell(clone.id, controller);
      },
    }],
    fullCard.name || removed.name || 'minion copy',
  );
}


// Dragonlord. "Before setup, set aside a Unique Dragon." SERVER-staged:
// SET_FIRST_PLAYER stages a durable modal pendingChoice (promptKind
// 'dragonlord', server dragonlordSetup.js) whose choices enumerate every
// catalog Unique Dragon; the open prompt blocks the start director's preGame
// auto-advance until answered. This fold renders it through the existing
// PileSearchDialog for the CHOOSER only, narrowing the DISPLAYED pool to the
// player's account collection (every sandbox flavour ignores ownership). A
// presentation filter; the server validates the pick is a Unique Dragon by
// construction. Owning no eligible Dragon auto-declines so the start can
// never wedge on an unanswerable prompt. Returns true while the prompt is a
// dragonlord prompt (handled here, not by the generic pickers).
export function foldDragonlordPromptFromMirror(host, pc) {
  const isDragon = !!pc && pc.promptKind === 'dragonlord';
  if (!isDragon) {
    // The tracked prompt resolved server-side (pick/decline committed).
    // close a picker that is still up. No decline reply: the server moved on.
    if (host.state.pendingDragonPick?.promptId) {
      host.setState({ pendingDragonPick: null, searchPile: null, searchQuery: '' });
    }
    return false;
  }
  // Solo: the pilot answers both seats; online only the addressee picks (the
  // opponent just waits. The pick is public once committed).
  if (!host.props?.isCasualSandbox && pc.seat !== host._localSeat?.()) return true;
  if (host.state.pendingDragonPick?.promptId === pc.promptId) return true;
  if (host._dragonlordDeclined?.has?.(pc.promptId)) return true;

  const catalog = host.props.sorceryCards || [];
  const serverPool = new Set((pc.choices || []).map((c) => c.value));
  const pool = dragonSetAsidePool({
    collection: host.props.profile?.collection,
    catalog,
    isCasualSandbox: host.props.isCasualSandbox,
    isSandboxMatch: host.props.isSandboxMatch,
    instanceMode: host.props.instanceMode,
  }).filter((full) => serverPool.has(full.unique_id));
  if (pool.length === 0) {
    (host._dragonlordDeclined || (host._dragonlordDeclined = new Set())).add(pc.promptId);
    host.logAction(k('Dragonlord: no Unique Dragon available'));
    host._sendMatchIntent?.(chooseIntent(pc.promptId, null, pc.seat));
    return true;
  }
  // Synthetic pile. Display-only card entries the PileSearchDialog can
  // render (it resolves art via cardId + printingId against the catalog).
  // Clicks route through _resolveDragonlordSetAsidePick, never through the
  // take-to-hand pile mutations.
  const pile = {
    id: 'dragonlord-set-aside',
    name: k('Unique Dragons'),
    rotated: pc.seat === 'p2',
    cards: pool.map((full) => {
      const printing = full.printings?.[0] || {};
      return {
        id: `dragonpick-${full.unique_id}`,
        cardId: full.unique_id,
        name: full.name,
        imageUrl: printing.image_url || null,
        printingId: printing.unique_id || null,
        foiling: printing.foiling || null,
      };
    }),
  };
  host.setState({
    searchPile: pile,
    searchQuery: '',
    pendingDragonPick: { promptId: pc.promptId, seat: pc.seat },
  });
  host.logAction(k('Dragonlord: choose a Unique Dragon to set aside'));
  return true;
}


// The pick replies to the server prompt. The dragonlord_companion resume
// stamps the PUBLIC dragonlordCompanion flag on the avatar (both seats fold
// it into the inspector). The champion is public information: a choice the
// opponent can never see is meaningless (you could claim any Dragon).
export function resolveDragonlordSetAsidePick(host, pickedCard) {
  const pending = host.state.pendingDragonPick;
  if (!pending?.promptId) return;
  const full = host._getFullCard(pickedCard.cardId);
  if (!isUniqueDragon(full)) {
    toast.error(t('Pick a Unique Dragon.'));
    return;
  }
  host._sendMatchIntent?.(chooseIntent(pending.promptId, pickedCard.cardId, pending.seat));
  host.logAction(k('Dragonlord reveals {card} as their champion'), { card: full.name });
  host.setState({ pendingDragonPick: null, searchPile: null, searchQuery: '' });
}


// Closing the picker declines the server prompt (it is optional). The
// set-aside is forfeited for this match and the start director's preGame
// advance unblocks. Called from the pile-search close path.
export function declineDragonlordSetAside(host, pending) {
  if (!pending?.promptId) return;
  (host._dragonlordDeclined || (host._dragonlordDeclined = new Set())).add(pending.promptId);
  host._sendMatchIntent?.(chooseIntent(pending.promptId, null, pending.seat));
}


// Dragonlord. "pay (3) to gain its abilities this turn." Wears the
// companion Dragon's rules identity (cardId / subTypes / keywords /
// Movement+N / Ranged. Every pipeline keys off these) for the turn
// while keeping Dragonlord's printed face, then reverts at end of
// turn via turnPhaseRunner. The (3) cost and once-per-turn mark were
// paid by the generic activation pipeline.
export function resolveDragonlordInvoke(host, avatar, controller) {
  const full = host._getFullCard(avatar._dragonlordCompanion);
  if (!full) return;
  const face = snapshotIdentity(avatar);
  const snapshot = applyMaskIdentity(avatar, full);
  // Keep the face AND the printed typeline: "gain its abilities" is
  // keywords + automation (which key off cardId / _printedKeywords /
  // Movement+N / Ranged), not the Dragon's name, art, subtypes, or
  // elements.
  avatar.name = face.name;
  avatar.imageUrl = face.imageUrl;
  avatar.imagePath = face.imagePath;
  avatar.foiling = face.foiling;
  avatar.subTypes = face.subTypes;
  avatar.elements = face.elements;
  avatar._dragonIdentitySnapshot = snapshot;
  avatar._dragonIdentityUntilTurnEnd = controller;
  host._refreshMovementHUD(avatar.id);
  host.recomputeStaticAuras?.();
  playSound('magic2');
  host.logAction(k('{name} gains the abilities of {card} this turn'), { name: cardLogLabel(face, 'Dragonlord'), card: full.name });
  if (host._authZonesEnabled?.()) {
    host._sendMatchCommand(authCardState.identityCommandFromCard(avatar, {
      dragonlord: true,
      original: avatar._dragonIdentitySnapshot,
    }));
  }
}


// End-of-turn revert for the Dragon identity. Restores the snapshot
// taken at invoke time and tells the opponent to do the same.
export function revertDragonlordIdentity(host, avatar) {
  const snapshot = avatar._dragonIdentitySnapshot;
  // Release the once-per-turn invoke lock unconditionally. If the
  // snapshot was lost (state adoption / resume edge), bailing with
  // the lock still set would grey the invoke button for the rest of
  // the match (canActivateEntry's dragonlord_invoke gate reads it).
  avatar._dragonIdentityUntilTurnEnd = null;
  if (!snapshot) return;
  restoreIdentity(avatar, snapshot);
  avatar._dragonIdentitySnapshot = null;
  host._refreshMovementHUD(avatar.id);
  host.recomputeStaticAuras?.();
  host.logAction(k("{name}'s borrowed abilities fade"), { name: avatar.name || 'Dragonlord' });
  if (host._authZonesEnabled?.()) {
    host._sendMatchCommand(authCardState.identityRestoreCommand(avatar.id));
  }
}


// Per-frame sync of the Dragonlord "ready" aura. A subtle amber glow
// sits under each locally-piloted Dragonlord whose invoke is payable
// RIGHT NOW (own turn + threshold met + mana available + not used
// yet. All delegated to _canActivateEntry so the aura and the action
// menu can never disagree). The companion is private information, so
// on the opponent's client `_dragonlordCompanion` is unset and the
// gate fails. The aura naturally renders only for the owner.
const DRAGONLORD_INVOKE_ENTRY = DRAGONLORD_RULES.extraActivatedAbilities
  .find((e) => e._builtin === 'dragonlord_invoke');

export function syncDragonlordReadyGlow(host, dt) {
  const glows = host.dragonlordGlowMeshes;
  if (!glows || !host.scene?.scene) return;

  const ready = new Set();
  for (const [cardId, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?._dragonlordCompanion || card.type !== 'Avatar') continue;
    if (!host.isCardOnLocalSide(card)) continue;
    if (!host._canActivateEntry(card, DRAGONLORD_INVOKE_ENTRY)) continue;
    ready.add(cardId);
  }

  for (const [cardId, effect] of glows) {
    if (!ready.has(cardId) || !host.meshes.has(cardId)) {
      disposeDragonlordReadyGlow(effect);
      glows.delete(cardId);
    }
  }
  for (const cardId of ready) {
    if (glows.has(cardId)) continue;
    const effect = createDragonlordReadyGlow();
    host.scene.scene.add(effect.points);
    glows.set(cardId, effect);
  }
  for (const [cardId, effect] of glows) {
    updateDragonlordReadyGlow(effect, dt, host.meshes.get(cardId));
  }
}


export function disposeDragonlordReadyGlows(host) {
  if (!host.dragonlordGlowMeshes) return;
  for (const [, effect] of host.dragonlordGlowMeshes) {
    disposeDragonlordReadyGlow(effect);
  }
  host.dragonlordGlowMeshes.clear();
}


// Per-frame sync of the dragon spirit echo. The merge visual that
// runs while a Dragonlord wears its companion's identity (see
// visualEffects/dragonSpiritEcho.js for the staging). Keyed off
// isDragonIdentityActive so it materialises on BOTH clients: the
// invoker via _dragonIdentitySnapshot, the opponent via the
// _dragonlordBorrow stamp from the avatar:mask broadcast. On revert
// the echo plays its outro (lifts away while fading) before being
// reaped.
export function syncDragonSpiritEchoes(host, dt) {
  const echoes = host.dragonSpiritEchoes;
  if (!echoes || !host.scene?.scene) return;

  for (const [cardId, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || echoes.has(cardId) || !isDragonIdentityActive(card)) continue;
    // While merged the instance's cardId IS the companion Dragon.
    // resolve the spirit art from its catalog printing.
    const dragonFull = host._getFullCard(card.cardId);
    const imageUrl = dragonFull?.printings?.[0]?.image_url
      ? resolveLocalImageUrl(dragonFull.printings[0].image_url)
      : null;
    const effect = createDragonSpiritEcho(imageUrl);
    host.scene.scene.add(effect.points);
    echoes.set(cardId, effect);
  }

  for (const [cardId, effect] of echoes) {
    const mesh = host.meshes.get(cardId);
    const card = mesh?.userData?.cardInstance;
    if (!mesh || !card) {
      disposeDragonSpiritEcho(effect);
      echoes.delete(cardId);
      continue;
    }
    if (!isDragonIdentityActive(card)) beginDragonSpiritEchoOutro(effect);
    if (!updateDragonSpiritEcho(effect, dt, mesh)) {
      disposeDragonSpiritEcho(effect);
      echoes.delete(cardId);
    }
  }
}


export function disposeDragonSpiritEchoes(host) {
  if (!host.dragonSpiritEchoes) return;
  for (const [, effect] of host.dragonSpiritEchoes) {
    disposeDragonSpiritEcho(effect);
  }
  host.dragonSpiritEchoes.clear();
}


// Find every imposter card on the table for the given controller
// ('p1' / 'p2') that is currently wearing a mask, and unmask each.
// In practice there is at most one masked imposter per controller.
// the iteration is defensive against edge cases (clones, tokens,
// mid-match controller swaps).
export function demaskOnAvatarDamage(host, controller) {
  const rotated = controller === 'p2';
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?._isImposter) continue;
    if (!!card.rotated !== rotated) continue;
    if (!card._originalIdentity) continue;
    host._takeOffMask(card);
  }
}


// v1.111. Flamecaller projectile resolution. Picked cell is one of
// the impact cells computed by _startFlamecallerFireBlast (first
// surface unit hit by a cardinal-direction projectile). Damage the
// unit there via the standard target-op pipeline so Ward / Death's
// Door / Avatar damage routing all behave uniformly.
export function resolveFlamecallerFireBlast(host, avatar, controller, impactCell, sourceName, damage) {
  if (!impactCell) {
    host.logAction(k('{name}: projectile picked an invalid direction (fizzles)'), { name: sourceName });
    return;
  }
  // Find the impact unit at the precomputed impact cell using the
  // same filter as the path scan (surface only, no Stealth, not the
  // shooter). The first such unit (any controller. Codex: projectile
  // can hit allies too) becomes the target.
  let target = null;
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (card.id === avatar.id) continue;
    if (!countsAsUnit(card)) continue;
    if (card.subsurface) continue;
    if (hasActiveStealth(card)) continue;
    const c = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (!c || c.col !== impactCell.col || c.row !== impactCell.row) continue;
    target = card;
    break;
  }
  if (!target) {
    host.logAction(k('{name}: projectile found no target at impact cell (fizzles)'), { name: sourceName });
    return;
  }
  host._executeTargetOp({
    op: OP.DAMAGE_TARGET,
    args: { amount: damage },
    sourceCard: avatar,
    controller,
  }, target);
  host.logAction(
    k('{name}: projectile hits {target} for {n}'),
    { name: sourceName, target: cardLogLabel(target, msg('unit')), n: damage },
  );
}


// v1.115. Avatar of Fire's "Tap → This turn, fire sites in your hand
// are also Fireballs." The Tap cost is already paid by `_activateAbility`
// before we land here. Installs a turn-scoped ignite flag on the
// controller; the actual cast-as-Fireball decision happens later when
// the player drops a Fire Site on the board (intercepted in the
// fresh-play commit loop, which pushes a choice prompt). The flag
// clears at the end of the controller's turn (via
// `_stepPruneExpiredEffects`) or if the avatar leaves play.
export function startAvatarOfFireIgnite(host, avatar, controller) {
  const sourceName = avatar.name || 'Avatar of Fire';
  if (!host._avatarOfFireIgnite) host._avatarOfFireIgnite = {};
  const turnNumber = host.state.turnNumbers?.[controller] || 0;
  host._avatarOfFireIgnite[controller] = { turnNumber, avatarCardId: avatar.id };
  host.logAction(k('{name}: fire sites in hand are now also Fireballs this turn'), { name: sourceName });
  toast.message(t('Click a red-glowing Fire Site in your hand to cast it as a Fireball.'));
}


// True iff the controller has an active Avatar of Fire ignite flag for
// their CURRENT turn AND the avatar that installed the flag is still
// in play. Stale flags (mismatched turn, or avatar gone) are treated
// as inactive; pruning happens lazily here so a paranoid call from
// any path is safe.
export function isAvatarOfFireIgniteActive(host, controller) {
  const ignite = host._avatarOfFireIgnite?.[controller];
  if (!ignite) return false;
  const turnNumber = host.state.turnNumbers?.[controller] || 0;
  if (ignite.turnNumber !== turnNumber) {
    delete host._avatarOfFireIgnite[controller];
    return false;
  }
  if (!host._findCardById(ignite.avatarCardId)) {
    delete host._avatarOfFireIgnite[controller];
    return false;
  }
  return true;
}


export function clearAvatarOfFireIgnite(host, controller) {
  if (host._avatarOfFireIgnite?.[controller]) {
    delete host._avatarOfFireIgnite[controller];
  }
}


// Open a projectile cell-pick rooted at the avatar's cell. Reuses the
// Flamecaller path scanner. Projectile codex is shared. Fireball
// deals 4 to the impact unit and 2 to each other unit at the impact
// cell; the splash math lives in `_resolveAvatarOfFireIgnite`.
export function startAvatarOfFireProjectile(host, avatar, controller, sourceName) {
  const sourceMesh = host.meshes.get(avatar.id);
  if (!sourceMesh) return;
  const startCell = host._cellAtWorld(sourceMesh.position.x, sourceMesh.position.z);
  if (!startCell) return;
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) return;
  const { colPositions, rowPositions } = computeGridPositions(grid);
  const totalCols = colPositions.length - 1;
  const totalRows = rowPositions.length - 1;

  const unitsByCell = new Map();
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (card.id === avatar.id) continue;
    if (card.type !== 'Minion' && card.type !== 'Avatar') continue;
    if (card.subsurface) continue;
    const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    const key = `${cell.col},${cell.row}`;
    const list = unitsByCell.get(key) || [];
    list.push(card);
    unitsByCell.set(key, list);
  }

  const isHittable = (card) => !hasActiveStealth(card);
  const paths = projectilePaths({
    startCell, totalCols, totalRows, unitsByCell, isHittable,
  });

  if (paths.length === 0) {
    host.logAction(k('{name}: no valid Fireball target (fizzles)'), { name: sourceName });
    return;
  }

  const validCells = new Set();
  for (const path of paths) {
    for (const c of path.pathCells) validCells.add(`${c.col},${c.row}`);
  }

  host._startCellPick(avatar, controller, OP.AVATAR_OF_FIRE_IGNITE, validCells, {
    description: t('Shoot a Fireball (4 impact, 2 splash)'),
    args: { amount: 4, splash: 2 },
    extra: { projectilePaths: paths },
  });
}


// Resolve Fireball damage at the picked impact cell: 4 to the impact
// unit (the projectile target), 2 to each other unit at that cell.
// Routes through `_executeTargetOp` so Ward / Death's Door / Avatar
// damage rules all behave uniformly.
export function resolveAvatarOfFireIgnite(host, avatar, controller, impactCell, sourceName, impactDamage, splashDamage) {
  if (!impactCell) {
    host.logAction(k('{name}: Fireball picked an invalid direction (fizzles)'), { name: sourceName });
    return;
  }
  let impactUnit = null;
  const otherUnits = [];
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (card.id === avatar.id) continue;
    if (card.type !== 'Minion' && card.type !== 'Avatar') continue;
    if (card.subsurface) continue;
    const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (!cell || cell.col !== impactCell.col || cell.row !== impactCell.row) continue;
    if (!impactUnit && !hasActiveStealth(card)) impactUnit = card;
    else otherUnits.push(card);
  }
  if (!impactUnit) {
    host.logAction(k('{name}: Fireball found no target at impact cell (fizzles)'), { name: sourceName });
    return;
  }
  host._executeTargetOp({
    op: OP.DAMAGE_TARGET,
    args: { amount: impactDamage },
    sourceCard: avatar,
    controller,
  }, impactUnit);
  host.logAction(
    k('{name}: Fireball hits {target} for {n}'),
    { name: sourceName, target: cardLogLabel(impactUnit, msg('unit')), n: impactDamage },
  );
  for (const other of otherUnits) {
    host._executeTargetOp({
      op: OP.DAMAGE_TARGET,
      args: { amount: splashDamage },
      sourceCard: avatar,
      controller,
    }, other);
    host.logAction(k('{name}: splash {n} to {target}'), { name: sourceName, n: splashDamage, target: cardLogLabel(other, msg('unit')) });
  }
}


// v1.115. Avatar of Fire: cast a Fire Site from hand as a Fireball.
// Called from the hand-card click handler when the controller has the
// ignite flag active and clicks one of their red-highlighted Fire
// Sites. Moves the chosen card hand → cemetery directly (Magic spells
// land in cemetery after resolution; the site never enters the realm,
// so the standard `sendCardToPile` Site-to-Rubble routing is bypassed
// We don't want a Rubble spawning anywhere on the board), then
// opens the projectile cell-pick rooted at the avatar's cell. The
// ignite flag stays active for the rest of the turn so additional
// fire sites can be cast as Fireballs without re-tapping the avatar
// Matches the literal "this turn, fire sites in your hand are also
// Fireballs" wording.
export function castFireSiteAsFireball(host, card) {
  const controller = card.rotated ? 'p2' : 'p1';
  if (!host._isAvatarOfFireIgniteActive(controller)) return;
  const catalog = host.props.sorceryCards || [];
  if (!isFireSite(card, catalog)) return;
  const ignite = host._avatarOfFireIgnite[controller];
  const avatar = host._findCardById(ignite.avatarCardId);
  if (!avatar) return;
  const sourceName = avatar.name || 'Avatar of Fire';
  // Auth (S2): the fire-site discard is the shot's COST and the SERVER pays it.
  // the fireball_shot intent names the site (discardCardId) and the server moves
  // it hand → cemetery in the same commit that stages the projectile prompt
  // (rejecting a non-fire-site / not-in-hand id). No local pre-move: a hand →
  // cemetery write landing before the intent would make the server see the cost
  // card as already gone and reject the honest shot. The resume owns the
  // 4 + 2-splash damage; the discard folds from the diff.
  if (host._authZonesEnabled?.()) {
    host.logAction(k('{name}: cast {card} as Fireball'), { name: sourceName, card: cardLogLabel(card, msg('a fire site')) });
    host._emitAbilityIntent?.(avatar, 'fireball_shot', false, { discardCardId: card.id });
    return;
  }
  host.removeFromHand(card);
  const cemetery = host.findOrCreateCemetery(controller === 'p2');
  cemetery.cards.push(card);
  host.updatePileMeshes();
  host.logAction(k('{name}: cast {card} as Fireball'), { name: sourceName, card: cardLogLabel(card, msg('a fire site')) });
  host._startAvatarOfFireProjectile(avatar, controller, sourceName);
}


// v1.103. Waveshaper first tap. Reuses _computeFloodCells with
// nearby=true (8-neighbor of body of water, codex "near").
export function startWaveshaperFlood(host, avatar, controller) {
  const valid = host._computeFloodCells(avatar, /* nearby = */ true);
  if (!valid) return;
  if (valid.size === 0) {
    host.logAction(k('{name}: no site near body of water to flood'), { name: avatar.name || 'Waveshaper' });
    return;
  }
  host._startCellPick(avatar, controller, OP.WAVESHAPER_FLOOD, valid);
}


// v1.103. Waveshaper flood + tap. Mirrors Avatar of Water's flood
// bookkeeping (unflood prior, flood new, track _floodedSiteId), then
// taps every non-Submerge minion sharing the cell and stamps
// _skipNextUntap on them (honored by _stepUntapAll).
export function resolveWaveshaperFlood(host, avatar, controller, cell, sourceName) {
  const priorId = avatar._floodedSiteId;
  if (priorId) {
    const priorMesh = host.meshes.get(priorId);
    const priorCard = priorMesh?.userData?.cardInstance;
    if (priorCard && (priorCard.statuses || []).includes('flooded')) {
      const changed = applyStatusToggle(priorCard, 'flooded', false);
      if (changed) {
        host.rebuildStatusBadges(priorCard.id);
        host._broadcastCardStatus(priorCard.id, 'flooded', false);
      }
    }
    avatar._floodedSiteId = null;
  }

  let pickedSite = null;
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    const cardCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
    if (cardCell && cardCell.col === cell.col && cardCell.row === cell.row) { pickedSite = card; break; }
  }
  if (!pickedSite) {
    host.logAction(k('{name}: no site at picked cell (fizzles)'), { name: sourceName });
    host.recomputeStaticAuras();
    return;
  }

  const changed = applyStatusToggle(pickedSite, 'flooded', true);
  if (changed) {
    host.rebuildStatusBadges(pickedSite.id);
    host._broadcastCardStatus(pickedSite.id, 'flooded', true);
  }
  avatar._floodedSiteId = pickedSite.id;
  host.logAction(k('{name}: floods {card} at [{cell}]'), { name: sourceName, card: cardLogLabel(pickedSite, msg('site')), cell: getCellNumber(cell) });

  const minionsAtCell = minionsToTapAtCell(host.meshes, host._cellAtWorld, cell.col, cell.row);
  for (const { card, mesh } of minionsAtCell) {
    if (!card.tapped) host._applyTap(card, mesh, true, { source: 'waveshaper_flood' });
    markSkipNextUntap(card);
    host._broadcastSkipNextUntap(card.id);
  }
  if (minionsAtCell.length > 0) {
    if (minionsAtCell.length === 1) {
      host.logAction(k('{name}: taps 1 non-Submerge minion there (skip next untap)'), { name: sourceName });
    } else {
      host.logAction(k('{name}: taps {n} non-Submerge minions there (skip next untap)'), { name: sourceName, n: minionsAtCell.length });
    }
  }
  host.recomputeStaticAuras();
}

// ── Animist: "You may cast magics in your hand as Spirits" ──────────
//
// An opt-in, repeatable, cost-free activated ability. Activating it ARMS
// a one-shot flag: the next Magic the controller plays from hand this turn
// enters the realm as a Spirit Minion (power = cost) instead of resolving
// on the storyline as a normal cast. The actual type flip is the pure
// `applyCastAsSpiritTransform`, gated on this flag at the hand → table
// boundary (cardPileLifecycle). Eligible hand magics glow (renderHand), and
// a storyline popup prompts the choice; the player drags a glowing magic to
// a cell to summon it. Re-activating while armed (or Cancel/ESC on the
// popup, or end of turn) disarms.
const ANIMIST_PROMPT_TYPE = 'pick_animist_spirit';

function animistEligibleHandMagics(host, controller) {
  const hand = controller === 'p2' ? host.state.handCards2 : host.state.handCards;
  return (hand || []).filter((c) => {
    if (c.faceDown) return false;
    const fc = host._getFullCard(c.cardId);
    return (fc?.type ?? fc?.guardian?.type ?? c.type) === 'Magic';
  });
}

export function armAnimistSpirit(host, avatarCard, controller) {
  if (!host._animistSpiritArm) host._animistSpiritArm = { p1: false, p2: false };
  // Re-activating while already armed toggles the ability off. The
  // ability's own row is the cancel affordance.
  if (host._animistSpiritArm[controller]) {
    disarmAnimistSpirit(host, controller);
    return;
  }
  if (animistEligibleHandMagics(host, controller).length === 0) {
    toast.message(t('No magic spells in hand to summon as a Spirit.'));
    return;
  }
  host._animistSpiritArm[controller] = true;
  host.logAction(k('{name} prepares to summon a magic as a Spirit'), { name: cardLogLabel(avatarCard, 'Animist') });
  host._pushStorylineEvents([{
    id: `${ANIMIST_PROMPT_TYPE}-${controller}`,
    promptType: ANIMIST_PROMPT_TYPE,
    cardInstance: avatarCard,
    cardName: avatarCard?.name || 'Animist',
    controller,
    description: t('Choose a magic in your hand to summon as a Spirit (power = cost). Drag a glowing card to a realm cell.'),
  }]);
  host.forceUpdate();
}

export function disarmAnimistSpirit(host, controller) {
  if (!host._animistSpiritArm?.[controller]) return;
  host._animistSpiritArm[controller] = false;
  const promptId = `${ANIMIST_PROMPT_TYPE}-${controller}`;
  host.setState((s) => ({
    pendingPrompts: (s.pendingPrompts || []).filter((p) => p.id !== promptId),
  }));
  host._closePrompt(promptId);
  host.forceUpdate();
}
